import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Skeleton } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useGetDropdownsQuery } from "../../redux/api/dropdowns";
import { setSelectedDropdownValues } from "../../redux/store/genericReportingSlice";

function ClientRegionDropdown(props) {
    const { includeTitle = true } = props;

    const dispatch = useDispatch();

    const selectedClient = useSelector(
        (state) => state.genericReporting.selectedDropdownValues['client_name']
    );
    const selectedValues = useSelector(
        (state) => state.genericReporting.selectedDropdownValues['client_region'] || []
    );
    const currentDropdownGroup = useSelector(
        (state) => state.genericReporting.currentDropdownGroup
    );

    const prevClientRef = useRef(selectedClient);
    const [hasClientJustChanged, setHasClientJustChanged] = useState(false);

    // Reset region selections when client changes
    useEffect(() => {
        if (prevClientRef.current !== selectedClient) {
            prevClientRef.current = selectedClient;
            setHasClientJustChanged(true);
            dispatch(
                setSelectedDropdownValues({ dropdownName: 'client_region', value: [] })
            );
        }
    }, [selectedClient, dispatch]);

    const queryArgs = useMemo(() => ({
        data: {
            dropdown_name: 'client_region',
            // Keep both keys for backward compatibility with server expectations
            ...(selectedClient ? { client_name: selectedClient, selected_client: selectedClient } : {}),
            ...(props.extraApiParams || {})
        },
        dropdown_group: currentDropdownGroup
    }), [selectedClient, currentDropdownGroup, props.extraApiParams]);

    const { data: apiData, isLoading, isError, isFetching } = useGetDropdownsQuery(queryArgs);

    // Apply defaults after fetch, especially after a client switch
    useEffect(() => {
        if (!apiData || isFetching || isError) {
            return;
        }

        const { options = [], default: defaultValue, multi_select } = apiData || {};

        if (options.length === 0) {
            return;
        }

        // If only one option, auto-select it
        if (options.length === 1) {
            const singleValue = multi_select ? [options[0]] : options[0];
            if (JSON.stringify(selectedValues) !== JSON.stringify(singleValue)) {
                dispatch(
                    setSelectedDropdownValues({ dropdownName: 'client_region', value: singleValue })
                );
            }
            setHasClientJustChanged(false);
            return;
        }

        // Prefer server-provided default when available and either:
        // - client just changed, or
        // - nothing currently selected
        if (defaultValue && (hasClientJustChanged || (Array.isArray(selectedValues) ? selectedValues.length === 0 : !selectedValues))) {
            const defaultValues = multi_select
                ? (Array.isArray(defaultValue) ? defaultValue : [defaultValue])
                : (Array.isArray(defaultValue) ? defaultValue[0] : defaultValue);

            if (JSON.stringify(selectedValues) !== JSON.stringify(defaultValues)) {
                dispatch(
                    setSelectedDropdownValues({ dropdownName: 'client_region', value: defaultValues })
                );
            }
            setHasClientJustChanged(false);
        }
    }, [apiData, isFetching, isError, dispatch, selectedValues, hasClientJustChanged]);

    // If there's only one option, don't render the dropdown
    if (!isFetching && !isError && apiData?.options && apiData.options.length === 1) {
        return null;
    }

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            marginLeft: "0.5vw",
            marginRight: "0.5vw",
            minWidth: "10vw",
        }}>
            {!isFetching && !isError && (apiData?.options?.length || 0) > 0 && (
                <>
                    {includeTitle && (
                        <h4 style={{ minHeight: "1.2em" }}>
                            {apiData?.dropdown_title ? apiData.dropdown_title : "Region"}
                        </h4>
                    )}
                    <Select
                        mode={apiData?.multi_select ? "multiple" : undefined}
                        style={{ width: apiData?.multi_select ? "12vw" : "10vw" }}
                        placeholder={"Select an option"}
                        value={selectedValues}
                        onChange={(value) => dispatch(setSelectedDropdownValues({ dropdownName: 'client_region', value }))}
                        loading={isFetching}
                        maxTagCount="responsive"
                        maxTagTextLength={6}
                        popupMatchSelectWidth={false}
                        showSearch
                        options={(apiData?.options || []).map((option) =>
                            typeof option === 'object' ? option : {
                                value: option,
                                label: option?.toString?.() ?? String(option),
                            }
                        )}
                    />
                </>
            )}
            {isFetching && (
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <h4 style={{ minHeight: "1.2em" }}>
                        <Skeleton.Input active size="small" style={{ width: '4vw', height: '1.2em' }} />
                    </h4>
                    <Select
                        mode="multiple"
                        style={{ width: '10vw' }}
                        placeholder="Select an option"
                        loading={isFetching}
                        maxTagCount="responsive"
                        showSearch
                    />
                </div>
            )}
        </div>
    );
}

export default ClientRegionDropdown;


